import type { CyanDelegationAuthority } from "./agentExecutionContract";

import type { AgentAssignmentService } from "./agentAssignmentService";

import type {
  ExecutionProgramRepository,
  PersistentExecutionProgram,
  ProgramWorkItemState,
} from "./executionProgramState";

import { ResourceLockManager } from "./resourceLockManager";

function dependenciesGreen(
  item: ProgramWorkItemState,
  program: PersistentExecutionProgram,
) {
  return item.workItem.dependencies.every((dependencyId) =>
    program.workItems.some(
      (candidate) =>
        candidate.workItem.id === dependencyId && candidate.state === "GREEN",
    ),
  );
}

export class MultiAgentScheduler {
  private readonly locks = new ResourceLockManager();

  constructor(
    private readonly programs: ExecutionProgramRepository,

    private readonly assignment: AgentAssignmentService,

    private readonly delegation: CyanDelegationAuthority,
  ) {}

  async tick(programId: string) {
    const program = await this.programs.get(programId);

    if (!program) {
      throw new Error(`Execution program ${programId} not found.`);
    }

    if (
      program.state === "PAUSED" ||
      program.state === "CANCELLED" ||
      program.state === "FAILED" ||
      program.state === "GREEN"
    ) {
      return program;
    }

    const mutable = program.workItems.map((item) => ({
      ...item,
    }));

    const ready = mutable.filter(
      (item) =>
        (item.state === "PENDING" || item.state === "READY") &&
        dependenciesGreen(item, program),
    );

    const dispatches: Promise<void>[] = [];

    for (const item of ready) {
      try {
        this.locks.acquire(item.workItem.id, item.workItem.resourceScope);
      } catch {
        continue;
      }

      let agent;

      try {
        agent = this.assignment.assign(item.workItem);
      } catch (error) {
        /*
         * One unassignable branch must not abort independent siblings.
         *
         * Assignment failure remains unfinished/recoverable work.
         * The item returns to READY, its resource lock is released,
         * and the scheduler continues evaluating the remaining graph.
         */
        item.state = "READY";

        item.summary =
          "Recoverable assignment failure: " +
          (error instanceof Error ? error.message : "Agent assignment failed.");

        this.locks.release(item.workItem.id);

        continue;
      }

      item.state = "RUNNING";

      item.assignedAgentId = agent.id;

      item.startedAt = new Date().toISOString();

      dispatches.push(
        this.delegation
          .dispatch({
            ...item.workItem,
            programId: program.id,
            assignedAgentId: agent.id,
          } as typeof item.workItem)
          .then((result) => {
            item.executionId = result.executionId;
          })
          .catch((error) => {
            /*
             * Dispatch failure is evidence, not task completion.
             *
             * Returning the item to READY lets the durable scheduler retry
             * after the implementation/provider state changes.
             */
            item.state = "READY";

            item.summary =
              "Recoverable dispatch failure: " +
              (error instanceof Error
                ? error.message
                : "Agent dispatch failed.");

            this.locks.release(item.workItem.id);
          }),
      );
    }

    await Promise.all(dispatches);

    const hasRunning = mutable.some((item) => item.state === "RUNNING");

    const hasFailed = mutable.some((item) => item.state === "FAILED");

    const allGreen =
      mutable.length > 0 && mutable.every((item) => item.state === "GREEN");

    const next: PersistentExecutionProgram = {
      ...program,

      workItems: mutable,

      state: allGreen
        ? "GREEN"
        : hasFailed
          ? "FAILED"
          : hasRunning
            ? "RUNNING"
            : "WAITING",

      updatedAt: new Date().toISOString(),
    };

    await this.programs.save(next);

    return next;
  }

  releaseResource(workItemId: string) {
    this.locks.release(workItemId);
  }
}
